import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import type { CourseSummary } from '../api/client.js';
import {
  buildCourseSwitchTarget,
  courseLevelLabel,
  isItemScopedPath,
  rememberCourse,
} from '../lib/courseSelection.js';

/**
 * The control the Academic Director asked for: **one CFA exam level at a time, chosen from the
 * screen** (2026-08-27, `#cfa-curriculum-updates`).
 *
 * > "three queues, one level at a time would be ideal if possible. Can handle multiple levels at
 * > once, but 3 queues would be the preference vs. everything combined within, the reason being that
 * > there are topics that are similar but not tested between levels and I wouldn't want to get my
 * > wires crossed upon review as the lines blur."
 *
 * Four properties carry that requirement, and each is pinned by a test in
 * `CourseLevelSelector.test.tsx`:
 *
 * 1. **Only granted courses are offered** — absent, not disabled. `GET /api/v1/courses` is already
 *    grant-filtered by `ContentService.listCourses`; the session's own list is the second check.
 * 2. **Choosing navigates AND remembers.** The URL answers "reload this page"; the remembered value
 *    answers "open a bare `/queue` next time". They are different reloads and both are needed.
 * 3. **An undeclared level says so**, rather than naming the course the application fell back to.
 *    Announcing "Level I" over a Level III lesson is exactly the wire-crossing he described.
 * 4. **A course the session cannot open is never substituted silently.** The fallback is correct —
 *    but it is stated, naming both courses, so a stale bookmark cannot quietly move him a level.
 *
 * **It is deliberately a course picker, not a level picker.** The catalog holds three Level I
 * vintages, two Level II vintages, Level III and a GMAT course, and the owner is granted all seven.
 * A control labelled "Level" over a list containing three Level I entries would be lying about what
 * the choice is; each option therefore leads with its declared level and ends with the course key
 * that disambiguates the vintage.
 */
export function CourseLevelSelector({
  catalog,
  courseKey,
  grantedCourseIds,
  declared = true,
  requestedCourseKey = null,
}: {
  /** The API's course catalog — the only place a level may come from. */
  catalog: CourseSummary[];
  /** The course in force, as resolved by `resolveCourseSelection`. */
  courseKey: string;
  /** Course ids this session holds a grant for. */
  grantedCourseIds: string[];
  /**
   * Did anything in the location actually NAME this course? `false` means the application chose it
   * because nothing said otherwise — fine on a course-wide list, misleading on one lesson.
   */
  declared?: boolean;
  /** What the location asked for, so a rejected request can be reported rather than swallowed. */
  requestedCourseKey?: string | null;
}) {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // The catalog is fetched asynchronously and the grants arrive with the session. Until both exist
  // there is nothing truthful to render, and an empty control is worse than no control.
  const options = grantedCourseIds.map(
    (id) => catalog.find((course) => course.id === id) ?? { id, title: id },
  );

  if (options.length === 0) {
    return null;
  }

  const activeCourse = catalog.find((course) => course.id === courseKey);
  const activeLevel = courseLevelLabel(activeCourse);
  /**
   * The location named a course, and the course in force is a different one. That only happens when
   * the session holds no grant for what was asked — `resolveCourseSelection` falls through. The
   * fallback is right; leaving it unsaid is not, because "a link he is sent cannot silently switch
   * him" is one of this build's three load-bearing behaviours.
   */
  const rejectedCourseKey =
    requestedCourseKey && courseKey && requestedCourseKey !== courseKey ? requestedCourseKey : null;

  /**
   * No level control on a screen about one piece of content.
   *
   * A review screen can hold unsaved comments, ratings, rejection reasons, follow-up tags and player
   * edits. `lib/unsavedWork.ts` is a probe registry consulted by the version banner, **not** a React
   * Router navigation blocker, and `EntityEditor` guards only navigation routed through its own
   * `requestNavigation`. A control in the shell that navigated away would therefore discard that work
   * with no prompt of any kind. Changing level is a thing you do from the queue.
   */
  const switchable = !isItemScopedPath(location.pathname) && options.length > 1;

  /**
   * `shortTitle` where the catalog supplies one ("CFA L1 2027"), because the control sits in a
   * header that also carries six navigation tabs: the full titles pushed "Audit Log" onto a second
   * line at 1280px. The **level leads** and the **course key closes**, and those two are what the
   * label has to carry — the level is the thing being chosen, and the key is what separates the
   * three Level I vintages from one another.
   */
  function optionLabel(course: CourseSummary) {
    const level = courseLevelLabel(course);
    const name = course.shortTitle || course.title || course.id;
    return level ? `${level} · ${name} (${course.id})` : `${name} (${course.id})`;
  }

  function switchTo(nextCourseKey: string) {
    if (!nextCourseKey || nextCourseKey === courseKey) return;
    rememberCourse(nextCourseKey);
    const target = catalog.find((course) => course.id === nextCourseKey);
    void navigate(
      buildCourseSwitchTarget(location.pathname, searchParams, nextCourseKey, {
        // Declared data, read from the catalog. `350`, `400` and `450` do not support structural
        // review, so a switch from `/queue/structural` must leave rather than land on a dead screen.
        ...(target?.supportsStructuralWorkflow === undefined
          ? {}
          : { supportsStructural: target.supportsStructuralWorkflow }),
      }),
    );
  }

  const identity = declared ? (
    <>
      <span className="text-sm font-bold text-slate-800">{activeCourse?.title ?? courseKey}</span>
      {activeLevel ? (
        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.08em] text-primary">
          {activeLevel}
        </span>
      ) : null}
      <span className="font-mono text-[0.7rem] text-slate-500">{courseKey}</span>
    </>
  ) : (
    <UndeclaredLevelChip />
  );

  return (
    <div className="flex shrink-0 items-center gap-2">
      <div
        className="flex items-baseline gap-2 rounded-lg bg-slate-100 px-3 py-1.5"
        data-testid="shell-course-identity"
        title={
          switchable
            ? `Every tab in this header opens ${activeCourse?.title ?? courseKey} (course ${courseKey}).`
            : 'Go back to the work queue to change which course you are reviewing.'
        }
      >
        {switchable ? (
          <label className="flex shrink-0 items-center gap-2">
            <span className="text-[0.65rem] font-bold uppercase tracking-[0.08em] text-slate-500">
              Reviewing
            </span>
            <select
              aria-label="Course and exam level being reviewed — changing this opens that course's work queue"
              className="max-w-[18rem] cursor-pointer truncate rounded-md border border-slate-300 bg-white py-1 pl-2 pr-7 text-sm font-bold text-slate-800 focus:border-primary focus:ring-1 focus:ring-primary"
              data-testid="course-level-select"
              onChange={(event) => switchTo(event.target.value)}
              value={declared ? courseKey : ''}
            >
              {/*
                The empty option exists ONLY in the undeclared state, so the control shows "no level
                declared" as its value rather than silently displaying whichever course the app fell
                back to. It is never selectable back into — choosing it would mean choosing "unknown".
              */}
              {declared ? null : <option disabled value="">— level not declared —</option>}
              {options.map((course) => (
                <option key={course.id} value={course.id}>{optionLabel(course)}</option>
              ))}
            </select>
          </label>
        ) : (
          identity
        )}
        {switchable && declared ? (
          <span className="font-mono text-[0.7rem] text-slate-500">{courseKey}</span>
        ) : null}
        {switchable && !declared ? <UndeclaredLevelChip /> : null}
      </div>
      {rejectedCourseKey ? (
        <span
          className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-[0.7rem] font-semibold text-amber-900"
          data-testid="course-request-rejected"
        >
          This link asked for course {rejectedCourseKey}, which you cannot open. Showing{' '}
          {activeCourse?.title ?? courseKey} ({courseKey}) instead.
        </span>
      ) : null}
    </div>
  );
}

/**
 * What the reviewer sees when a link named no level.
 *
 * It states the fact and deliberately names no course: the application's fallback is not evidence
 * about the content on screen, and printing it is how a Level III lesson came to be labelled
 * "Level I".
 */
function UndeclaredLevelChip() {
  return (
    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.08em] text-amber-800">
      Level not declared by this link
    </span>
  );
}
